import { SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { v4 as uuidv4 } from 'uuid';
import { SEGMENTS_DEFAULT_COLOR } from '../../constants/colors';
import { BrandKitStore, PlatformBrandKitStore } from '../../types/brand-kit-types';
import { Clip, AlignedRecordings } from '../../types/clip-types';
import { VideoElement } from '../../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { CompositeSegment } from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SceneChanges } from '../../types/scene-changes';
import { SegmentType } from '../../types/Segment/Type';
import { SpeakersAnalysis } from '../../types/speakers-analysis';
import { VideoFeed } from '../../types/video-feed-types';
import {
  getLayoutChangesAndPlayRanges,
  getLayoutChangesAndPlayRangesWithSplits
} from './getLayoutChangesAndPlayRanges';
import { createScenesFromSceneOverrides } from './scenes';
import { getVideoElements } from './videoFeedAdapters';
import { hasMultipleClipSegments, isSplitsInTimelineEnabledPure } from '../../utils/clipUtils';

export function createMainCompositeSegment({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  facialRecognitionToDefaultPosition,
  videoFeeds,
  speakerAnalysis,
  alignedRecordings,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPersonContainerEnabled = false,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  alignedRecordings?: AlignedRecordings[];
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPersonContainerEnabled?: boolean;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}): CompositeSegment {
  const fromFrame = clip.asset_metadata.start * fps;
  const toFrame = clip.asset_metadata.end * fps;
  const videos: VideoElement[] = getVideoElements({
    videoFeeds,
    clip,
    fromFrame,
    toFrame,
    isPlatformBrandKitEnabled,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization
  });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;
  const roundCorners = !!asset_metadata.round_corners;
  const pipShape = asset_metadata.pip_shape ?? undefined;
  const pipPosition = asset_metadata.pip_position ?? undefined;

  let scenes = asset_metadata.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: alignedRecordings ?? []
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRanges({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    roundCorners,
    pipShape,
    pipPosition,
    scenes,
    videos,
    videoFeeds,
    speakerAnalysis,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled,
    isScreenshareDetectionEnabled,
    isPersonContainerEnabled
  });

  return {
    id: 'segment',
    name: 'Composite',
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage ||
          asset_metadata.magicLayout?.backgroundColor ||
          SEGMENTS_DEFAULT_COLOR
      }
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    }
  };
}
export function createCompositeFromOldComposite({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  oldCompositeSegment,
  facialRecognitionToDefaultPosition,
  videoFeeds,
  speakerAnalysis,
  alignedRecordings,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPersonContainerEnabled = false,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  oldCompositeSegment?: CompositeSegment;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  alignedRecordings?: AlignedRecordings[];
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPersonContainerEnabled?: boolean;
  isSpeakerDirectionEnabled?: boolean;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
  isSplitsTranslationEnabled: boolean;
}): CompositeSegment {
  // A highlight reel with multiple source windows is always treated as a splits clip — checked before
  // the translation guard so translated reels still go through the splits path.
  const splitsEnabled =
    isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled }) || hasMultipleClipSegments(clip);
  if (!splitsEnabled) {
    throw new Error('Splits are not enabled for this clip');
  }

  if (!oldCompositeSegment || !oldCompositeSegment.segment_metadata) {
    throw new Error('Old composite segment not found or no metadata found');
  }

  const fromFrame = oldCompositeSegment.segment_metadata.start * fps;
  const toFrame = oldCompositeSegment.segment_metadata.end * fps;
  const videos: VideoElement[] = getVideoElements({
    videoFeeds,
    clip,
    fromFrame,
    toFrame,
    isPlatformBrandKitEnabled,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization
  });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;
  const roundCorners = !!asset_metadata.round_corners;
  const pipShape = asset_metadata.pip_shape ?? undefined;
  const pipPosition = asset_metadata.pip_position ?? undefined;

  let scenes = oldCompositeSegment.segment_metadata?.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: alignedRecordings ?? []
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRangesWithSplits({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    roundCorners,
    pipShape,
    pipPosition,
    scenes,
    videos,
    oldSegment: oldCompositeSegment,
    videoFeeds,
    speakerAnalysis,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled,
    isScreenshareDetectionEnabled,
    isPersonContainerEnabled
  });

  return {
    id: oldCompositeSegment.id,
    name: oldCompositeSegment.name,
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage ||
          asset_metadata.magicLayout?.backgroundColor ||
          SEGMENTS_DEFAULT_COLOR
      }
    },
    segment_metadata: oldCompositeSegment?.segment_metadata || {
      start: clip.asset_metadata.start,
      end: clip.asset_metadata.end,
      deletes: clip.asset_metadata.deletes || {},
      edits: clip.asset_metadata.edits || {},
      merges: clip.asset_metadata.merges || [],
      excludes: clip.asset_metadata.excludes || [],
      maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
      captions_lang: clip.asset_metadata.original_lang,
      captions: clip.asset_metadata.captions || [],
      fitMode: clip.asset_metadata.fit,
      scenes: clip.asset_metadata.scenes || [],
      duration: clip.asset_metadata.duration || 0
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    }
  };
}

export function createMainCompositeSegmentWithMetaData({
  clip,
  fps,
  duration,
  offset,
  sceneOverrides,
  facialRecognitionToDefaultPosition,
  videoFeeds,
  speakerAnalysis,
  alignedRecordings,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPersonContainerEnabled = false,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization
}: {
  clip: Clip;
  fps: number;
  duration: number;
  offset: number;
  sceneOverrides?: SceneChanges;
  facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse;
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  alignedRecordings?: AlignedRecordings[];
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPersonContainerEnabled?: boolean;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}): CompositeSegment {
  const fromFrame = clip.asset_metadata.start * fps;
  const toFrame = clip.asset_metadata.end * fps;
  const videos: VideoElement[] = getVideoElements({
    videoFeeds,
    clip,
    fromFrame,
    toFrame,
    isPlatformBrandKitEnabled,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization
  });

  const asset_metadata = clip.asset_metadata;
  const layout = asset_metadata.layout;
  const size = asset_metadata.size;
  const fitMode = asset_metadata.fit;
  const hideBorders = !!asset_metadata.hide_borders;
  const roundCorners = !!asset_metadata.round_corners;
  const pipShape = asset_metadata.pip_shape ?? undefined;
  const pipPosition = asset_metadata.pip_position ?? undefined;

  let scenes = asset_metadata.scenes || [];
  if (sceneOverrides) {
    scenes = createScenesFromSceneOverrides({
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      fps,
      clipStartFrame: fromFrame,
      clipEndFrame: toFrame,
      videos,
      recordings: alignedRecordings ?? []
    });
  }

  const { layoutChanges, playRanges } = getLayoutChangesAndPlayRanges({
    clip,
    fps,
    layout,
    size,
    fitMode,
    hideBorders,
    roundCorners,
    pipShape,
    pipPosition,
    scenes,
    videos,
    videoFeeds,
    speakerAnalysis,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled,
    isScreenshareDetectionEnabled,
    isPersonContainerEnabled
  });

  return {
    id: uuidv4(),
    name: 'Composite',
    type: SegmentType.COMPOSITE,
    scenes,
    videos,
    layoutChanges,
    playRanges,
    fromFrame: offset,
    toFrame: offset + duration,
    details: {
      background: {
        type: asset_metadata.magicLayout?.backgroundImage ? 'IMAGE_URL' : 'COLOR',
        value:
          asset_metadata.magicLayout?.backgroundImage ||
          asset_metadata.magicLayout?.backgroundColor ||
          SEGMENTS_DEFAULT_COLOR
      }
    },
    projectId: clip.content.project.id,
    contentId: clip.content.id,
    maxBounds: {
      fromFrame,
      toFrame
    },
    currentBounds: {
      fromFrame,
      toFrame
    },
    segment_metadata: {
      start: clip.asset_metadata.start,
      end: clip.asset_metadata.end,
      deletes: clip.asset_metadata.deletes || {},
      edits: clip.asset_metadata.edits || {},
      merges: clip.asset_metadata.merges || [],
      excludes: clip.asset_metadata.excludes || [],
      maxVideoDuration: clip.asset_metadata.maxVideoDuration || 0,
      captions_lang: clip.asset_metadata.original_lang,
      captions: clip.asset_metadata.captions || [],
      fitMode: clip.asset_metadata.fit,
      scenes: clip.asset_metadata.scenes || [],
      duration: clip.asset_metadata.duration || 0
    }
  };
}
